<script setup lang="ts">
import { ChevronsUpDown, LogOut, Sun, Moon, Edit, Info } from "lucide-vue-next"
import { useAuthStore } from "@/stores/auth"
import { useRouter } from "vue-router"
import { useTheme } from "@/composables/useTheme"
import { useI18n } from "@/composables/useI18n"

const { t } = useI18n()

import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"

defineProps<{
  user: {
    email: string
    firstName: string
    lastName: string
  }
  version?: string
  buildTime?: string
}>()

const { isMobile } = useSidebar()

const authStore = useAuthStore()
const router = useRouter()
const { theme, toggleTheme } = useTheme()

const handleEditAccount = () => {
  router.push("/dashboard/edit-account")
}

const handleLogout = async () => {
  await authStore.logout()
  router.push("/login")
}
</script>

<template>
  <SidebarMenu>
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <SidebarMenuButton
            size="lg"
            class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
          >
            <Avatar class="h-8 w-8 rounded-lg">
              <AvatarFallback class="rounded-lg">
                {{ user.firstName?.charAt(0) }}{{ user.lastName?.charAt(0) }}
              </AvatarFallback>
            </Avatar>
            <div class="grid flex-1 text-left text-sm leading-tight">
              <span class="truncate font-medium">{{ user.firstName }} {{ user.lastName }}</span>
              <span class="truncate text-xs">{{ user.email }}</span>
            </div>
            <ChevronsUpDown class="ml-auto size-4" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          class="w-[--reka-dropdown-menu-trigger-width] min-w-56 rounded-lg"
          :side="isMobile ? 'bottom' : 'right'"
          align="end"
          :side-offset="4"
        >
          <DropdownMenuLabel class="p-0 font-normal">
            <div class="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <Avatar class="h-8 w-8 rounded-lg">
                <AvatarFallback class="rounded-lg">
                  {{ user.firstName?.charAt(0) }}{{ user.lastName?.charAt(0) }}
                </AvatarFallback>
              </Avatar>
              <div class="grid flex-1 text-left text-sm leading-tight">
                <span class="truncate font-semibold">{{ user.firstName }} {{ user.lastName }}</span>
                <span class="truncate text-xs">{{ user.email }}</span>
                <span
                  v-if="version"
                  class="mt-0.5 inline-flex items-center gap-1 text-[10px] text-muted-foreground"
                >
                  <Info class="h-3 w-3" />
                  v{{ version }}<template v-if="buildTime">@{{ buildTime }}</template>
                </span>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuItem @click="handleEditAccount">
            <Edit class="h-4 w-4" />
            {{ t("auth.account.editTitle") }}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem @click="toggleTheme">
            <Sun v-if="theme === 'dark'" class="h-4 w-4" />
            <Moon v-else class="h-4 w-4" />
            <span>{{ theme === "dark" ? t("common.theme.lightMode") : t("common.theme.darkMode") }}</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem @click="handleLogout">
            <LogOut />
            {{ t("auth.logout") }}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  </SidebarMenu>
</template>
